import moment from 'moment-timezone';
import Vue from 'vue';
import PredictPriceChangeModalComponent from '@/components/Modals/PredictPriceChangeModal.vue';

/**
 * PredictPriceChangeModal Utility Class
 * Modal untuk menampilkan perubahan harga Predict ID dan memberikan opsi Confirm/Decline
 * 
 * Usage:
 * const result = await PredictPriceChangeModal.fire({
 *   store: store,
 *   global: global,
 *   userData: userData,
 *   costagency: costagency,
 *   to: to,
 *   from: from,
 *   router: router
 * });
 * 
 * Result:
 * {
 *   isConfirmed: true/false/null, // true = confirm, false = decline, null = close
 *   costagency: {...} // updated costagency object
 * }
 */
class PredictPriceChangeModal {
    /**
     * Generate log description for price change
     * @param {Object} params - Parameters for log generation
     * @param {Object} params.userData - User data object
     * @param {boolean} params.hasMinCostMonthChange - Whether monthly fee changed
     * @param {boolean} params.hasMaxCampaignsChange - Whether max campaigns changed
     * @param {string} params.fromMinCostMonth - Previous monthly fee value
     * @param {string} params.fromMaxCampaigns - Previous max campaigns value
     * @param {string} params.finalMinCostMonth - Final monthly fee value
     * @param {string} params.finalMaxCampaigns - Final max campaigns value
     * @returns {string} Log description string
     */
    static generateLogDescription(params) {
        const {
            userData,
            fromMinCostMonth,
            fromMaxCampaigns,
            finalMinCostMonth,
            finalMaxCampaigns,
            actionType,
        } = params;

        const changes = [
            `Monthly Fee From ${fromMinCostMonth} to ${finalMinCostMonth}`,
            `Monthly Allocation From ${fromMaxCampaigns} campaigns to ${finalMaxCampaigns} campaigns`,
        ];
        
        const changesText = changes.join(' | ');
        const userName = userData.name || '';
        const userEmail = userData.email || '';
        const companyName = userData.company_name || '';
        const companyId = userData.company_id || '';
        
        return `The user has agreed to the Marketing Services Agreement Predict Price Change ${actionType} . ${changesText} | Name: ${userName} | Email: ${userEmail} | Company Name: ${companyName} | Company Id: ${companyId} | menu:predict-change-price`;
    }

    /**
     * Main method to show price change modal
     * @param {Object} options - Configuration options
     * @param {Object} options.store - Vuex store instance
     * @param {Object} options.global - Global state object
     * @param {Object} options.userData - User data object
     * @param {Object} options.costagency - Cost agency object with PredictMaxCampaignsPrev and/or PredictMinCostMonthPrev
     * @param {Object} options.to - Router to object
     * @param {Object} options.from - Router from object
     * @param {Object} options.router - Router instance
     * @returns {Promise} Promise that resolves with result object
     */
    static async fire(options = {}) {
        const { store, global, userData, costagency, calculationPricingPredict, to, from, router } = options;

        // Validate required parameters
        if (!store || !global || !userData || !costagency) {
            throw new Error('PredictPriceChangeModal.fire: Missing required parameters');
        }

        // Check if costagency has predict data
        if (!costagency.predict || !costagency.predict.Prepaid) {
            return Promise.resolve({
                isConfirmed: null,
                costagency: costagency
            });
        }

        const predictPrepaid = costagency.predict.Prepaid;
        const hasMaxCampaignsPrev = predictPrepaid.hasOwnProperty('PredictMaxCampaignsPrev') && 
                                    predictPrepaid.PredictMaxCampaignsPrev !== null && 
                                    predictPrepaid.PredictMaxCampaignsPrev !== undefined;
        const hasMinCostMonthPrev = predictPrepaid.hasOwnProperty('PredictMinCostMonthPrev') && 
                                    predictPrepaid.PredictMinCostMonthPrev !== null && 
                                    predictPrepaid.PredictMinCostMonthPrev !== undefined;

        // If no previous values, return
        if (!hasMaxCampaignsPrev && !hasMinCostMonthPrev) {
            return Promise.resolve({
                isConfirmed: null,
                costagency: costagency
            });
        }

        // Get current and previous values
        const currentMaxCampaigns = predictPrepaid.PredictMaxCampaigns || '0';
        const currentMinCostMonth = predictPrepaid.PredictMinCostMonth || '0';
        const prevMaxCampaigns = hasMaxCampaignsPrev ? predictPrepaid.PredictMaxCampaignsPrev : currentMaxCampaigns;
        const prevMinCostMonth = hasMinCostMonthPrev ? predictPrepaid.PredictMinCostMonthPrev : currentMinCostMonth;

        // Get pricing info
        const betaFeature = global.betaFeature || {};
        const predictModuleName = betaFeature.predict_module && betaFeature.predict_module.name || 'Predict Module';
        const currentMonthlyFee = parseFloat(currentMinCostMonth) || 0;
        const currentMonthlyAllocation = currentMaxCampaigns + ' campaigns';
        const currentOverageRate = parseFloat(predictPrepaid.PredictOverageRate) || 0;

        // time
        const today = moment.tz('America/Chicago').toDate();
        const firstBillingDate = moment.tz('America/Chicago').add(30, 'days').toDate();

        // Build module data for display
        const module = {
            name: predictModuleName,
            monthlyFee: currentMonthlyFee,
            monthlyAllocation: currentMonthlyAllocation,
            overageBilling: true,
            overageRate: currentOverageRate,
            commitmentTerm: 12,
            billingFrequency: 'Monthly in advance',
            effectiveDate: today,
            firstBillingDate: firstBillingDate,
            annualCommitmentTotal: currentMonthlyFee * 12,
            // Previous values for display
            prevMaxCampaigns: prevMaxCampaigns,
            prevMinCostMonth: prevMinCostMonth,
            hasMaxCampaignsChange: hasMaxCampaignsPrev,
            hasMinCostMonthChange: hasMinCostMonthPrev
        };

        return new Promise((resolve) => {
            // Create Vue component instance
            const ModalComponent = Vue.extend(PredictPriceChangeModalComponent);
            const modalInstance = new ModalComponent({
                propsData: {
                    modules: [module],
                    priceChangeData: {
                        hasMaxCampaignsChange: hasMaxCampaignsPrev,
                        hasMinCostMonthChange: hasMinCostMonthPrev,
                        currentMaxCampaigns: currentMaxCampaigns,
                        currentMinCostMonth: currentMinCostMonth,
                        prevMaxCampaigns: prevMaxCampaigns,
                        prevMinCostMonth: prevMinCostMonth,
                        calculationPricingPredict: calculationPricingPredict
                    }
                }
            });

            // Mount component
            modalInstance.$mount();
            document.body.appendChild(modalInstance.$el);

            // Set internalShow to true
            Vue.nextTick(() => {
                modalInstance.internalShow = true;
                modalInstance.$forceUpdate();
            });

            // Handle modal result
            modalInstance.$once('confirm', async () => {
                // Set loading state
                modalInstance.internalIsLoading = true;
                modalInstance.$forceUpdate();

                try {
                    // Confirm: Keep current values, remove Prev fields
                    const updatedCostagency = JSON.parse(JSON.stringify(costagency));
                    if (updatedCostagency.predict && updatedCostagency.predict.Prepaid) {
                        if (updatedCostagency.predict.Prepaid.hasOwnProperty('PredictMaxCampaignsPrev')) {
                            Vue.delete(updatedCostagency.predict.Prepaid, 'PredictMaxCampaignsPrev');
                        }
                        if (updatedCostagency.predict.Prepaid.hasOwnProperty('PredictMinCostMonthPrev')) {
                            Vue.delete(updatedCostagency.predict.Prepaid, 'PredictMinCostMonthPrev');
                        }
                    }

                    // Update costagency via API
                    await store.dispatch('updateGeneralSetting', {
                        companyID: userData.company_id,
                        actionType: 'customsmtpmodule',
                        comsetname: 'costagency',
                        comsetval: updatedCostagency,
                        source: 'confirm_predict_config_change',
                    });

                    // Save user logs after successful update
                    const logDescription = this.generateLogDescription({
                        userData,
                        fromMinCostMonth: prevMinCostMonth,
                        fromMaxCampaigns: prevMaxCampaigns,
                        finalMinCostMonth: currentMinCostMonth,
                        finalMaxCampaigns: currentMaxCampaigns,
                        actionType: 'Confirm',
                    });
                    await store.dispatch('onSaveUserLogs', {
                        desc: logDescription,
                        action: 'Marketing Services Agreement - Predict Price Change - Confirm',
                        target_id: userData.id,
                    });

                    // Close modal only on success
                    modalInstance.internalShow = false;
                    setTimeout(() => {
                        modalInstance.$destroy();
                        if (modalInstance.$el && modalInstance.$el.parentNode) {
                            modalInstance.$el.parentNode.removeChild(modalInstance.$el);
                        }
                    }, 300);

                    // notification success
                    Vue.prototype.$notify({
                        type: 'success',
                        message: 'Predict price change confirmed successfully.',
                        icon: 'fas fa-check-circle'
                    });

                    resolve({
                        isConfirmed: true,
                        costagency: updatedCostagency
                    });
                } catch (error) {
                    // Show error notification
                    modalInstance.internalIsLoading = false;
                    const errorMessage = (error && error.response && error.response.data && error.response.data.message) || 
                                        (error && error.message) || 
                                        'Something went wrong, please try again later';
                    Vue.prototype.$notify({
                        type: 'danger',
                        message: errorMessage,
                        icon: 'fas fa-exclamation-triangle'
                    });
                    console.error('Error in confirm:', error);
                    
                    // Modal tetap terbuka, jangan di-close
                } finally {
                    // Reset loading state
                    modalInstance.internalIsLoading = false;
                    modalInstance.$forceUpdate();
                }
            });

            modalInstance.$once('decline', async () => {
                // Set loading state
                modalInstance.internalIsLoading = true;
                modalInstance.$forceUpdate();

                try {
                    // Decline: Restore previous values, remove Prev fields
                    const updatedCostagency = JSON.parse(JSON.stringify(costagency));
                    let finalMaxCampaigns = currentMaxCampaigns;
                    let finalMinCostMonth = currentMinCostMonth;
                    
                    if (updatedCostagency.predict && updatedCostagency.predict.Prepaid) {
                        if (hasMaxCampaignsPrev) {
                            updatedCostagency.predict.Prepaid.PredictMaxCampaigns = prevMaxCampaigns;
                            finalMaxCampaigns = prevMaxCampaigns;
                            Vue.delete(updatedCostagency.predict.Prepaid, 'PredictMaxCampaignsPrev');
                        }
                        if (hasMinCostMonthPrev) {
                            updatedCostagency.predict.Prepaid.PredictMinCostMonth = prevMinCostMonth;
                            finalMinCostMonth = prevMinCostMonth;
                            Vue.delete(updatedCostagency.predict.Prepaid, 'PredictMinCostMonthPrev');
                        }
                    }

                    // Update costagency via API
                    await store.dispatch('updateGeneralSetting', {
                        companyID: userData.company_id,
                        actionType: 'customsmtpmodule',
                        comsetname: 'costagency',
                        comsetval: updatedCostagency,
                    });

                    // Save user logs after successful update
                    // For decline, show change from current back to previous values
                    const logDescription = this.generateLogDescription({
                        userData,
                        fromMinCostMonth: currentMinCostMonth,
                        fromMaxCampaigns: currentMaxCampaigns,
                        finalMinCostMonth: finalMinCostMonth,
                        finalMaxCampaigns: finalMaxCampaigns,
                        actionType: 'Decline',
                    });
                    await store.dispatch('onSaveUserLogs', {
                        desc: logDescription,
                        action: 'Marketing Services Agreement - Predict Price Change - Decline',
                        target_id: userData.id,
                    });

                    // Close modal only on success
                    modalInstance.internalShow = false;
                    setTimeout(() => {
                        modalInstance.$destroy();
                        if (modalInstance.$el && modalInstance.$el.parentNode) {
                            modalInstance.$el.parentNode.removeChild(modalInstance.$el);
                        }
                    }, 300);

                    // notification success
                    Vue.prototype.$notify({
                        type: 'success',
                        message: 'Predict price change declined successfully.',
                        icon: 'fas fa-check-circle'
                    });

                    resolve({
                        isConfirmed: false,
                        costagency: updatedCostagency
                    });
                } catch (error) {
                    // Show error notification
                    modalInstance.internalIsLoading = false;
                    const errorMessage = (error && error.response && error.response.data && error.response.data.message) || 
                                        (error && error.message) || 
                                        'Something went wrong, please try again later';
                    Vue.prototype.$notify({
                        type: 'danger',
                        message: errorMessage,
                        icon: 'fas fa-exclamation-triangle'
                    });
                    console.error('Error in decline:', error);
                    
                    // Modal tetap terbuka, jangan di-close
                } finally {
                    // Reset loading state
                    modalInstance.internalIsLoading = false;
                    modalInstance.$forceUpdate();
                }
            });

            modalInstance.$once('close', () => {
                // Close: Do nothing, just close modal
                modalInstance.internalShow = false;
                setTimeout(() => {
                    modalInstance.$destroy();
                    if (modalInstance.$el && modalInstance.$el.parentNode) {
                        modalInstance.$el.parentNode.removeChild(modalInstance.$el);
                    }
                }, 300);

                resolve({
                    isConfirmed: null,
                    costagency: costagency
                });
            });
        });
    }
}

export default PredictPriceChangeModal;

